import { navigationNamespace } from "@/store/modules/navigation";
import { CensusData, Patient } from "@/store/modules/patient/types";
import { Permission, userNamespace } from "@/store/modules/user";
import { User } from "@/store/modules/user/types";
import _ from "lodash";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";

const patientNamespace = { namespace: "patient" };

const DEFAULT_NPO_TIME = 11;

@Component({})
export default class ioBalance extends Vue {
  @Getter("user", userNamespace)
  user!: User;

  @Action("updateSettings", patientNamespace)
  updateSettings!: Function;

  @Getter("current", patientNamespace)
  current!: Patient;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("demo", userNamespace)
  demo!: boolean;

  @Getter("darkMode", navigationNamespace)
  darkMode!: boolean;

  @Watch("current", { immediate: true })
  setSavingNormalizeFalse() {
    this.savingNormalize = false;
  }

  constructor() {
    super(...arguments);
    this.npoTimeItems = [];
    for (let i = 0; i <= 24; i++) {
      this.npoTimeItems.push(i);
    }
  }

  headers = [
    "I/O Balance",
    "= Added Fluid",
    "- Insensible Loss",
    "- EBL x 3",
    "- Surgery Loss",
    "- Urine Total",
    "± Normalize"
  ];

  get atoms() {
    return this.current.body.atoms;
  }

  weightFactor = 0;

  get patientTable() {
    const currentPatient = this.current;
    const atoms = currentPatient.body.atoms;
    const current = currentPatient.body.current;
    const weight = atoms.weight.value;

    if (weight <= 10) {
      this.weightFactor = 4 * weight;
    } else if (weight <= 20) {
      this.weightFactor = 20 + 2 * weight;
    } else {
      this.weightFactor = 40 + weight;
    }
    const surgeryHours = atoms.surgeryHours.value;
    const insensibleLoss =
      (atoms.npoTime.value + surgeryHours) * this.weightFactor;
    const surgeryLoss = current.ThirdSpaceLoss.value * weight * surgeryHours;
    const heartVolumeSign = atoms.ioBalance.value > 0 ? "+" : "";
    const heartVolumeValue = atoms.ioBalance.value.toFixed(0);
    const heartVolume = `${heartVolumeSign} ${heartVolumeValue}`;
    const estimatedBloodLoss = atoms.ebl.value;

    const urine =
      atoms.urine.value +
      _.get(this.current.body, "prior.atoms.urine.value", 0);

    return [
      heartVolume,
      atoms.addedFluid.value,
      insensibleLoss,
      estimatedBloodLoss * 3,
      surgeryLoss,
      urine,
      current.VolumeOffset.value * -1
    ];
  }

  cannotEditToolTip(field: string = "") {
    return !this.canAlter
      ? {
          content: "Current user does not have permission to " + field + ".",
          openOnHover: true
        }
      : {};
  }

  get canAlter() {
    if (this.user.rights.includes(Permission.NO_EDIT)) {
      return false;
    } else if (this.user.rights.includes(Permission.ADMIN)) {
      return true;
    }

    const census: CensusData = this.current.census;

    // in the room
    if (this.user.room === census.room) {
      return true;
    }

    // one of the staff logged into the case.
    if (
      census.staff
        .map(s => s.userID.toLowerCase())
        .includes(this.user.userID.toLowerCase())
    ) {
      return true;
    }

    return false;
  }

  savingNormalize = false;

  normalize() {
    this.savingNormalize = true;

    if (this.demo) {
      return;
    }
    const value =
      this.current.body.current.VolumeOffset.value +
      this.current.body.atoms.ioBalance.value;

    this.updateSettings({
      id: this.current.census.roomSys,
      settings: { VolumeOffset: value }
    });
    this.savingNormalize = false;
  }

  get normalizedValue() {
    return Math.round(
      _.get(this.current, "body.current.VolumeOffset.value", 0)
    );
  }

  get allowUndoNormalize() {
    return this.normalizedValue !== 0;
  }

  async undoNormalize() {
    this.savingNormalize = true;
    if (this.demo) {
      return;
    }
    this.updateSettings({
      id: this.current.census.roomSys,
      settings: { VolumeOffset: 0 }
    });
    this.savingNormalize = false;
  }

  get npoTimeValue() {
    const npoValue = Math.round(
      _.get(this.current, "body.atoms.npoTime.value", -1)
    );
    if (npoValue === -1) {
      return DEFAULT_NPO_TIME;
    }
    return npoValue;
  }

  setNpo(value: number) {
    if (this.demo) {
      return;
    }
    this.updateSettings({
      id: this.current.census.roomSys,
      settings: { NpoTime: value }
    });
  }

  get thirdSpaceLossValue() {
    return _.get(this.current, "body.current.ThirdSpaceLoss.value", 0);
  }

  setThirdSpaceLoss(value: number) {
    if (this.demo) {
      return;
    }
    this.updateSettings({
      id: this.current.census.roomSys,
      settings: { ThirdSpaceLoss: value }
    });
  }

  thirdSpaceLossItems = [
    { value: 0, text: "None" },
    { value: 3, text: "Light = 3 ml/kg/hr" },
    { value: 5, text: "Moderate = 5 ml/kg/hr" },
    { value: 7, text: "Severe = 7 ml/kg/hr" }
  ];

  npoTimeItems: Array<number>;
}
